import { ToolPointer } from './tool';
import { PlaneToolBase } from './planeToolBase';
import { add, dist, mul, normalize, sub, Vec3, dot, cross, eq } from '../core/vec';
import { formatLength, parseLength } from '../core/units';
import { addLineSegment } from '../core/lines';

const ARC_SEGMENTS = 12;

/** Bogen-Werkzeug (A): Start, Ende, Wölbung — kreisförmiger Bogen durch 3 Punkte. */
export class ArcTool extends PlaneToolBase {
  readonly id = 'arc';
  readonly label = 'Bogen';
  readonly shortcut = 'A';
  readonly cursor = 'crosshair';
  readonly hint = 'Startpunkt, Endpunkt, dann Wölbung ziehen · Auf einer Fläche zeichnen teilt diese';

  private start: Vec3 | null = null;
  private end: Vec3 | null = null;
  private current: Vec3 | null = null;

  enter(): void {
    this.cancel();
  }

  exit(): void {
    this.ctx.preview.clear();
    this.start = null;
    this.end = null;
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    const snap = this.getSnap(e);
    this.showSnapDot(snap);
    if (!this.start) {
      this.current = snap.point;
    } else if (!this.end) {
      this.pickPlane(this.start, e);
      this.current = this.snapInPlane(e, snap, [this.start]);
      this.showInference();
      this.ctx.preview.line(this.start, this.current, 0x333333);
    } else {
      this.current = this.snapInPlane(e, snap, [this.start, this.end]);
      this.drawPreview();
      this.ctx.ui.setStatus(
        `Wölbung: ${formatLength(this.sagitta(), this.ctx.units())} · Zahl tippen = Wölbung`
      );
    }
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e); // Frischen Snap für die aktuelle Position holen
    const p = this.current ?? this.snap!.point;
    if (!this.start) {
      const snap = this.snap!;
      this.start = snap.point;
      this.adoptPlane(snap);
    } else if (!this.end) {
      if (eq(this.start, p, 1e-6)) return;
      this.end = p;
    } else {
      this.commit();
    }
    this.ctx.requestUpdate();
  }

  /** Sehne und Abstand des Cursorpunkts zur Sehnenmitte (Wölbung). */
  private sagitta(): number {
    if (!this.start || !this.end || !this.current) return 0;
    const mid = mul(add(this.start, this.end), 0.5);
    return dist(mid, this.current);
  }

  private arcPoints(): Vec3[] {
    return arcThrough(this.start!, this.end!, this.current!) ?? [];
  }

  private drawPreview(): void {
    if (!this.start || !this.end || !this.current) return;
    const pts = this.arcPoints();
    this.ctx.preview.polyline(pts, 0x333333);
  }

  private commit(): void {
    if (!this.start || !this.end || !this.current) return;
    const pts = this.arcPoints();
    if (pts.length < 3) {
      this.ctx.ui.setStatus('Wölbung liegt auf der Sehne — kein Bogen möglich');
      return;
    }
    // Offener Bogen (ohne Sehne) — schließt er mit vorhandenen Kanten einen Ring, entsteht eine Fläche
    this.ctx.beginEdit();
    for (let i = 0; i + 1 < pts.length; i++) addLineSegment(this.ctx.model, pts[i]!, pts[i + 1]!);
    this.start = null;
    this.end = null;
    this.current = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.start !== null && this.end !== null;
  }

  vcbDisplay(): string | null {
    if (!this.start || !this.end) return null;
    return formatLength(this.sagitta(), this.ctx.units());
  }

  applyVcb(raw: string): void {
    if (!this.start || !this.end || !this.plane) return;
    const sag = parseLength(raw, this.ctx.units());
    if (sag === null || Math.abs(sag) < 1e-6) return;
    const f = this.frame();
    const mid = mul(add(this.start, this.end), 0.5);
    const chord = normalize(sub(this.end, this.start));
    const perp = normalize(cross(f.n, chord));
    this.current = add(mid, mul(perp, sag));
    this.commit();
  }

  cancel(): void {
    this.start = null;
    this.end = null;
    this.current = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}

/**
 * Kreisbogen von `a` nach `b`, der durch `p` läuft (wie SketchUps Bogen: Start, Ende, Wölbung).
 * Liefert null, wenn die drei Punkte (fast) auf einer Geraden liegen.
 */
export function arcThrough(a: Vec3, b: Vec3, p: Vec3, segmentsPerHalfTurn = ARC_SEGMENTS): Vec3[] | null {
  const ab = sub(b, a);
  const ap = sub(p, a);
  const L = Math.hypot(ab.x, ab.y, ab.z);
  if (L < 1e-9) return null;
  const nRaw = cross(ab, ap);
  const nLen = Math.hypot(nRaw.x, nRaw.y, nRaw.z);
  if (nLen < 1e-9 * L * Math.max(1e-9, Math.hypot(ap.x, ap.y, ap.z)) || nLen < 1e-12) return null;
  const n = mul(nRaw, 1 / nLen);
  const u = mul(ab, 1 / L);
  const v = cross(n, u);
  // 2D in der Bogenebene: A = (0,0), B = (L,0), P = (px, py) mit py ≠ 0
  const px = dot(ap, u), py = dot(ap, v);
  if (Math.abs(py) < 1e-9) return null;
  const cx = L / 2;
  const cy = ((px - cx) * (px - cx) + py * py - cx * cx) / (2 * py);
  const r = Math.hypot(cx, cy);
  const TAU = Math.PI * 2;
  const mod = (x: number) => ((x % TAU) + TAU) % TAU;
  const ta = Math.atan2(-cy, -cx);
  const tb = Math.atan2(-cy, L - cx);
  const tp = Math.atan2(py - cy, px - cx);
  const ccw = mod(tb - ta);
  // Richtung so wählen, dass der Bogen durch p läuft
  const throughCcw = mod(tp - ta) < ccw;
  const dir = throughCcw ? 1 : -1;
  const sweep = throughCcw ? ccw : TAU - ccw;
  const segments = Math.max(4, Math.round((sweep / Math.PI) * segmentsPerHalfTurn));
  const pts: Vec3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = ta + dir * (sweep * i) / segments;
    const x = cx + r * Math.cos(t), y = cy + r * Math.sin(t);
    pts.push(add(a, add(mul(u, x), mul(v, y))));
  }
  // Endpunkte exakt setzen (keine Rundungsfehler an vorhandenen Ecken)
  pts[0] = a;
  pts[pts.length - 1] = b;
  return pts;
}
